import React from 'react';
import { Box, TextField } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import {
    analyticsDirtyIconSx,
    getAnalyticsDirtyIconColor,
} from '../../styles/analyticsMsiStyles';

const normalizePercentInput = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Number(parsed.toFixed(6));
};

export default function ToolbarInputs({
    disabled = false,
    maxPosSizeStr,
    normalVolStr,
    setMaxPosSizeStr,
    setNormalVolStr,
    setLocalMaxPosSize,
    setLocalNormalVol,
    serverMaxPosSize,
    serverNormalVol,
    maxPosSizeIsDirty,
    normalVolIsDirty,
    onUndoMaxPosSize,
    onUndoNormalVol,
    onMaxPosSizeCommit,
    onNormalVolCommit,
}) {
    const handleMaxPosSizeChange = (e) => {
        setMaxPosSizeStr(e.target.value);
    };

    const handleNormalVolChange = (e) => {
        setNormalVolStr(e.target.value);
    };

    const handleMaxPosSizeBlur = () => {
        const trimmed = maxPosSizeStr.trim();
        const serverPercent = normalizePercentInput((serverMaxPosSize ?? 0) * 100) ?? 0;

        if (trimmed === '') {
            setMaxPosSizeStr(serverPercent.toString());
            setLocalMaxPosSize(serverMaxPosSize);
            return;
        }

        const value = parseFloat(trimmed);
        if (Number.isNaN(value)) {
            setMaxPosSizeStr(serverPercent.toString());
            setLocalMaxPosSize(serverMaxPosSize);
            return;
        }

        const clamped = normalizePercentInput(Math.min(100, Math.max(0, value))) ?? serverPercent;
        const nextFraction = normalizePercentInput(clamped / 100);
        if (nextFraction == null) {
            setMaxPosSizeStr(serverPercent.toString());
            setLocalMaxPosSize(serverMaxPosSize);
            return;
        }
        setLocalMaxPosSize(nextFraction);
        setMaxPosSizeStr(clamped.toString());
        if (clamped !== serverPercent) {
            onMaxPosSizeCommit?.(nextFraction);
        }
    };

    const handleNormalVolBlur = () => {
        const trimmed = normalVolStr.trim();
        const serverPercent = normalizePercentInput((serverNormalVol ?? 0) * 100) ?? 0;

        if (trimmed === '') {
            setNormalVolStr(serverPercent.toString());
            setLocalNormalVol(serverNormalVol);
            return;
        }

        const value = parseFloat(trimmed);
        if (Number.isNaN(value)) {
            setNormalVolStr(serverPercent.toString());
            setLocalNormalVol(serverNormalVol);
            return;
        }

        const clamped = normalizePercentInput(Math.min(100, Math.max(0, value))) ?? serverPercent;
        const nextFraction = normalizePercentInput(clamped / 100);
        if (nextFraction == null) {
            setNormalVolStr(serverPercent.toString());
            setLocalNormalVol(serverNormalVol);
            return;
        }
        setLocalNormalVol(nextFraction);
        setNormalVolStr(clamped.toString());
        if (clamped !== serverPercent) {
            onNormalVolCommit?.(nextFraction);
        }
    };

    const sharedTextFieldSx = {
        width: 112,
    };

    const sharedInputProps = {
        inputMode: 'decimal',
        pattern: "[0-9]*\\.?[0-9]*",
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative' }}>
                <TextField
                    label="Max Pos Size (%)"
                    size="small"
                    variant="outlined"
                    value={maxPosSizeStr}
                    onChange={handleMaxPosSizeChange}
                    onBlur={handleMaxPosSizeBlur}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur();
                        }
                    }}
                    inputProps={sharedInputProps}
                    disabled={disabled}
                    sx={sharedTextFieldSx}
                />
                {maxPosSizeIsDirty && (
                    <UndoIcon
                        onClick={onUndoMaxPosSize}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            color: (theme) => getAnalyticsDirtyIconColor(theme, 'orange'),
                            ...analyticsDirtyIconSx,
                        }}
                    />
                )}
            </Box>

            <Box sx={{ position: 'relative' }}>
                <TextField
                    label="Normal Vol (%)"
                    size="small"
                    variant="outlined"
                    value={normalVolStr}
                    onChange={handleNormalVolChange}
                    onBlur={handleNormalVolBlur}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur();
                        }
                    }}
                    inputProps={sharedInputProps}
                    disabled={disabled}
                    sx={sharedTextFieldSx}
                />
                {normalVolIsDirty && (
                    <UndoIcon
                        onClick={onUndoNormalVol}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            color: (theme) => getAnalyticsDirtyIconColor(theme, 'orange'),
                            ...analyticsDirtyIconSx,
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}
